/**
 * V1.5 Briefings Library — flache Liste aller KI-Briefings.
 * Quelle: Implementation Brief §7.7.
 *
 * Filter: Projekt-Picker (alle Customer/Key), Datum-Range. Klick öffnet
 * Modal mit voller Briefing.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';
import { Modal } from '../../components/v2/modal.js';
import { api } from '../../lib/api.js';
import { useFeatureFlag, useFeatureFlags } from '../../lib/features.js';

type Briefing = {
  ki_briefing_id: string;
  project_id: string;
  project_key: string;
  customer_name: string;
  body: string;
  model: string | null;
  created_at: string;
  created_by: string | null;
};

export function BriefingsLibraryPage() {
  const { t, i18n } = useTranslation();
  // F2: Feature-Flag-Guard. Bei deaktiviertem Flag (oder solange noch ladend
  // bekannt) auf Mission Control umleiten — Deeplink-Schutz, nicht nur Nav.
  const flags = useFeatureFlags();
  const briefingsEnabled = useFeatureFlag('briefings_library');
  const briefingsQuery = useQuery({
    queryKey: ['v2-briefings-all'],
    queryFn: () => api.listAllKiBriefings() as Promise<{ items: Briefing[] }>,
  });

  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [openId, setOpenId] = useState<string | null>(null);

  const items = briefingsQuery.data?.items ?? [];

  const projectOptions = useMemo(() => {
    const map = new Map<
      string,
      { project_id: string; project_key: string; customer_name: string }
    >();
    for (const b of items) {
      if (!map.has(b.project_id)) {
        map.set(b.project_id, {
          project_id: b.project_id,
          project_key: b.project_key,
          customer_name: b.customer_name,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.project_key.localeCompare(b.project_key));
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((b) => {
      if (projectFilter && b.project_id !== projectFilter) return false;
      if (dateFrom && new Date(b.created_at) < new Date(dateFrom)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${b.project_key} ${b.customer_name} ${b.body}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, projectFilter, dateFrom, search]);

  const openBriefing = openId ? items.find((b) => b.ki_briefing_id === openId) : null;

  if (!flags.isLoading && !briefingsEnabled) return <Navigate to="/" replace />;

  return (
    <div className="max-w-[var(--canvas-max)] mx-auto px-2 pb-12 space-y-6">
      <header className="pt-6 animate-fade-up">
        <div
          className="text-[10.5px] uppercase tracking-[0.14em] mb-3"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.briefings_lib.eyebrow', 'Briefings · Bibliothek')}
        </div>
        <h1
          className="font-medium leading-[1.05] tracking-tight"
          style={{
            fontFamily: 'var(--f-display)',
            color: 'var(--ink)',
            fontSize: 'clamp(22px, 2.4vw, 36px)',
          }}
        >
          {t('v2.briefings_lib.headline_lead', 'Alle')}{' '}
          <em
            className="not-italic"
            style={{ color: 'var(--primary)', fontStyle: 'italic', fontWeight: 400 }}
          >
            {t('v2.briefings_lib.headline_emphasis', 'KI-Briefings')}
          </em>
          {t('v2.briefings_lib.headline_trail', ' durchsuchbar.')} · {items.length}
        </h1>
        <p className="text-sm mt-2 max-w-[640px]" style={{ color: 'var(--ink-3)' }}>
          {t(
            'v2.briefings_lib.subtitle',
            'Cross-Projekt-Liste aller generierten KI-Mission-Briefings. Filter + Volltext-Modal.',
          )}
        </p>
      </header>

      {/* Filter */}
      <div
        className="rounded-xl border p-4 flex items-center gap-3 flex-wrap animate-fade-up-d1"
        style={{ background: 'var(--surface)', borderColor: 'var(--line-soft)' }}
      >
        <input
          type="text"
          placeholder={t('v2.briefings_lib.search_placeholder', 'Volltext im Briefing…')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 rounded-md text-sm flex-1 min-w-[200px] outline-none"
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--line-soft)',
            color: 'var(--ink)',
          }}
        />
        <select
          value={projectFilter ?? ''}
          onChange={(e) => setProjectFilter(e.target.value || null)}
          className="px-3 py-1.5 rounded-md text-sm outline-none"
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--line-soft)',
            color: 'var(--ink)',
          }}
        >
          <option value="">{t('v2.briefings_lib.filter_all_projects', 'Alle Projekte')}</option>
          {projectOptions.map((p) => (
            <option key={p.project_id} value={p.project_id}>
              {p.project_key} · {p.customer_name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="px-3 py-1.5 rounded-md text-sm outline-none"
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--line-soft)',
            color: 'var(--ink)',
          }}
        />
        <span
          className="text-[10px] ml-auto"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {filtered.length} / {items.length}
        </span>
      </div>

      {/* Tabelle */}
      <div
        className="rounded-xl border overflow-hidden animate-fade-up-d2"
        style={{ background: 'var(--surface)', borderColor: 'var(--line-soft)' }}
      >
        {filtered.length === 0 ? (
          <p className="p-6 text-sm text-center" style={{ color: 'var(--ink-3)' }}>
            {t('v2.briefings_lib.empty', 'Keine Briefings gefunden.')}
          </p>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {filtered.map((b) => (
              <li key={b.ki_briefing_id} style={{ borderColor: 'var(--line-soft)' }}>
                <button
                  type="button"
                  onClick={() => setOpenId(b.ki_briefing_id)}
                  className="w-full grid grid-cols-[120px_180px_1fr_120px] items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-[var(--surface-2)]"
                >
                  <span
                    style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: 'var(--ink-2)' }}
                  >
                    {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' }).format(
                      new Date(b.created_at),
                    )}
                  </span>
                  <span style={{ color: 'var(--ink)', fontSize: 13 }}>
                    <span
                      style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)' }}
                    >
                      {b.project_key}
                    </span>
                    {' · '}
                    {b.customer_name}
                  </span>
                  <span className="truncate text-xs" style={{ color: 'var(--ink-3)' }}>
                    {b.body.slice(0, 140)}
                    {b.body.length > 140 ? '…' : ''}
                  </span>
                  <span
                    className="text-[10px] text-right"
                    style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
                  >
                    {b.model ?? '—'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal */}
      {openBriefing && <BriefingModal briefing={openBriefing} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function BriefingModal({ briefing: b, onClose }: { briefing: Briefing; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      // Projektschluessel + Kunde benennen den Dialog besser als der Seitentitel.
      labelledBy="briefings-lib-modal-title"
      panelClassName="w-[720px] max-w-[92vw] max-h-[80vh] overflow-hidden flex flex-col"
    >
      <header
        className="flex items-start justify-between gap-3 p-5 border-b"
        style={{ borderColor: 'var(--line-soft)' }}
      >
        <div>
          <div
            className="text-[10.5px] uppercase tracking-[0.14em] mb-1"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {new Intl.DateTimeFormat(i18n.language, {
              dateStyle: 'medium',
              timeStyle: 'short',
            }).format(new Date(b.created_at))}{' '}
            · {b.model ?? t('v2.detail.audit_author_unknown', 'unbekannt')}
          </div>
          <Link
            id="briefings-lib-modal-title"
            to={`/projects/${b.project_id}`}
            className="text-base font-medium"
            style={{ color: 'var(--ink)', textDecoration: 'none' }}
          >
            <span style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: 'var(--ink-3)' }}>
              {b.project_key}
            </span>{' '}
            · {b.customer_name}
          </Link>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('v2.briefings_lib.modal_close', 'Schließen')}
          className="text-xl px-2"
          style={{ color: 'var(--ink-3)' }}
        >
          ×
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-5">
        <p
          className="leading-relaxed"
          style={{
            fontFamily: 'var(--f-display)',
            fontStyle: 'italic',
            fontSize: 18,
            color: 'var(--ink)',
          }}
        >
          {b.body}
        </p>
      </div>
    </Modal>
  );
}
